import { FormEvent } from "react";
import { FormikErrors } from "formik";
import { Button, Image, Input } from "@atoms";
import { AuthSection } from "@organisms";
import { useTranslation } from "react-i18next";

export interface FormValues {
  emailAddress: string;
}

export type ForgotPasswordPageTemplateProps = {
  values: FormValues;
  errors: FormikErrors<FormValues>;
  loading: boolean;
  handleInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleSignInRedirect: () => void;
  handleSubmit: (e?: FormEvent<HTMLFormElement> | undefined) => void;
};

export const ForgotPasswordPageTemplate: React.FC<ForgotPasswordPageTemplateProps> = ({
  values,
  errors,
  loading,
  handleInputChange,
  handleSignInRedirect,
  handleSubmit,
}: ForgotPasswordPageTemplateProps): JSX.Element => {
  const { t } = useTranslation();
  const { emailAddress } = values;
  const { emailAddress: emailAddressError } = errors;

  return (
    <div className="min-h-screen flex">
      <AuthSection />

      {/* Right side - Form */}
      <div className="w-full lg:w-1/2 min-h-screen flex items-center justify-center bg-white">
        <div className="w-full max-w-md px-8">
          {/* Title and subtitle */}
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              {t("auth.forgotPassword.title")}
            </h1>
            <p className="text-gray-600 text-sm">
              {t("auth.forgotPassword.subtitle")}
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <Input
                name="emailAddress"
                variant="text"
                onChange={handleInputChange}
                placeholder={t("auth.forgotPassword.emailPlaceholder")}
                value={emailAddress}
                errorMessage={emailAddressError}
              />
            </div>

            {/* Submit button */}
            <Button
              type="submit"
              variant="primary"
              label={t("auth.forgotPassword.submit")}
              stretch
              color="primary-medium"
              hoverColor="primary-dark"
              textColor="white"
              loading={loading}
            />

            {/* Back to Sign In button */}
            <Button
              variant="secondary"
              label={t("auth.forgotPassword.backToSignIn")}
              stretch
              color="neutral-darkest"
              onClick={handleSignInRedirect}
            />
          </form>
        </div>
      </div>
    </div>
  );
};
